import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Ticket, Users, Megaphone,
  Settings, LogOut, Menu, X, Building2, ChevronRight, Home, Calculator, Wallet, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

const adminNav = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
  { label: 'Units', icon: Home, path: '/admin/units' },
  { label: 'Generate Dues', icon: Calculator, path: '/admin/generate-dues' },
  { label: 'Expenditure', icon: Wallet, path: '/admin/expenditure' },
  { label: 'Transactions', icon: Receipt, path: '/admin/transactions' },
  { label: 'Tickets', icon: Ticket, path: '/admin/tickets' },
  { label: 'Residents', icon: Users, path: '/admin/residents' },
  { label: 'Announcements', icon: Megaphone, path: '/admin/announcements' },
  { label: 'Settings', icon: Settings, path: '/admin/settings' },
];

const superAdminNav = [
  { label: 'Platform Settings', icon: Settings, path: '/superadmin' },
];

export default function AdminLayout({ children, isSuperAdmin = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = isSuperAdmin ? superAdminNav : adminNav;
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar flex flex-col fixed md:sticky top-0 h-screen z-50 ${sidebarOpen ? 'open' : ''} md:!w-[260px]`}>
        <div className="p-5 flex items-center gap-3 border-b border-slate-700/50">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
            <Building2 size={18} className="text-white" />
          </div>
          <div>
            <h1 className="font-bold text-sm text-white font-['Outfit']">FreeCoffee</h1>
            <p className="text-[10px] text-emerald-400">Your Free CommunityOS</p>
          </div>
          <button className="ml-auto md:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {!isSuperAdmin && user?.role === 'super_admin' && (
            <Link to="/superadmin" className="nav-item mb-2 text-emerald-400 hover:text-emerald-300">
              <Settings size={18} />
              <span>Platform Settings</span>
              <ChevronRight size={14} className="ml-auto" />
            </Link>
          )}
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-700/50">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-emerald-600 text-white text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 md:px-8 py-3 flex items-center gap-4">
          <button className="md:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={20} className="text-slate-600" />
          </button>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-emerald-600 text-white text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm text-slate-700">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-slate-500">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut size={14} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
